import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { usePathname, useSearchParams } from 'next/navigation'
import React, { useEffect, useMemo, useState } from 'react'

import InfoIcon from '../../../../public/svg/outline-info.svg'
import { ContactService } from '../../../utilities/salesForceForm'
import { PhnxButton } from '../../01-atoms/Button/Button'
import { PhnxForminput } from '../../01-atoms/Forminput/Forminput'
import { PhnxFormselect } from '../../01-atoms/Formselect/Formselect'
import { PhnxFormSlider } from '../../01-atoms/FormSlider/FormSlider'
import { CalcFormField, Rating } from './Blok'
import styles from './CalculatorForm.module.css'
import Results from './components/Results/Results'
import industryDataImport from './data/industry-data.json'
import { IndustryData, IndustryDataEntry } from './types'

const industryData = industryDataImport as unknown as IndustryData

interface CalculatorFormProps {
  formFields: CalcFormField[]
  ratings: Rating[]
  disclaimer: React.ReactNode
  initialFormData?: Record<string, string>
  showResults?: boolean
  hideResultsScreen?: boolean
  onFormSubmit?: (formData: Record<string, string>) => void
  printData?: any
  onDownload?: () => Promise<void>
}

interface SelectOption {
  value: string
  label: string
}

const CalculatorForm: React.FC<CalculatorFormProps> = ({
  formFields = [],
  ratings,
  disclaimer,
  initialFormData,
  showResults = false,
  hideResultsScreen = false,
  onFormSubmit,
  printData,
  onDownload,
}) => {
  if (!formFields || formFields.length === 0) {
    console.warn('CalculatorForm: formFields prop is required but was not provided or is empty')
    return null
  }

  const [currentStep, setCurrentStep] = useState(1)
  const [calculatedScores, setCalculatedScores] = useState({
    profitability: '',
    capitalManagement: '',
    growthReadiness: '',
  })
  const [scoresReady, setScoresReady] = useState(false)
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [isClientSide, setIsClientSide] = useState(false)
  const [highestStepReached, setHighestStepReached] = useState(1)

  const searchParams = useSearchParams()
  const pathname = usePathname()

  // Check if we're on client side
  useEffect(() => {
    setIsClientSide(true)
  }, [])

  // Group fields by steps
  const steps = useMemo(() => {
    let currentStepNumber = 1
    return formFields.reduce((acc: { [key: number]: CalcFormField[] }, field) => {
      if (field.component === 'calcPageBreak') {
        currentStepNumber = parseInt(field.title.replace('Step ', ''))
        acc[currentStepNumber] = []
      } else {
        if (!acc[currentStepNumber]) {
          acc[currentStepNumber] = []
        }
        acc[currentStepNumber].push(field)
      }
      return acc
    }, {})
  }, [formFields])

  // Helper function to generate consistent field names
  const getFieldName = (field: CalcFormField) => {
    // if key is undefined, return an empty string
    if (!field.key) {
      return ''
    }
    return field.key.toLowerCase().replace(/\s+/g, '_')
  }
  const totalSteps = useMemo(() => Object.keys(steps).length, [steps])

  // Transform formFields from Storyblok format to the expected format
  const processedFormFields = useMemo(() => {
    return formFields.reduce((acc: any, field) => {
      if (field.component === 'calcFormHeading') {
        acc[`h_${field.title.toLowerCase().replace(/\s+/g, '')}`] = {
          type: 'heading',
          title: field.title,
          width: field.width || 12,
        }
      } else if (field.component === 'calcFormField') {
        const fieldKey = field.title.toLowerCase().replace(/\s+/g, '_')

        let fieldOptions: SelectOption[] = []

        // Handle options based on field type
        if (field.type === 'dropdown') {
          if (field.industryDataOptions) {
            // Handle industry data
            fieldOptions = Object.entries(industryData).map(([key, industry]) => ({
              value: key,
              label: industry.name,
            }))
          } else if (field.options) {
            // Handle other dropdowns like business size
            fieldOptions = field.options.split('\n').map((opt) => {
              const [key, ...labelParts] = opt.split(':')
              return {
                value: key.trim(),
                label: labelParts.join(':').trim(),
              }
            })
          }
        }

        acc[fieldKey] = {
          type: field.type,
          title: field.title,
          width: parseInt(field.width) || 12,
          info: field.info,
          category: field.category,
          prefix: field.prefix || '',
          suffix: field.prefix || '',
          order: field.order,
          options: fieldOptions,
        }
      }
      return acc
    }, {})
  }, [formFields])

  // Use processedFormFields instead of formFields in your component
  const [formData, setFormData] = useState(() => {
    if (initialFormData) {
      return initialFormData
    }
    return {}
  })

  // Transform industry data into sorted array for select
  const sortedIndustries = useMemo(() => {
    return Object.entries(industryData)
      .map(([key, industry]) => ({
        _uid: key,
        name: industry.name,
      }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [])

  const handleInputChange = (name: string, value: string) => {
    setFormData((prev) => {
      const newState = {
        ...prev,
        [name]: value,
      }

      // If industry or business size changes, initialize slider values
      if (name === 'industrySelect' || name === 'sizeOfBusiness') {
        // Only proceed if both industry and size are selected
        if (newState.industrySelect && newState.sizeOfBusiness) {
          // Find all slider fields
          const sliderFields = formFields.filter(
            (field) => field.component === 'calcFormField' && field.type === 'slider'
          )

          // Set default values for each slider
          sliderFields.forEach((field) => {
            const fieldKey = field.key
            if (!fieldKey) return

            // Only set if the field doesn't already have a value
            if (!prev[fieldKey]) {
              const range = getFieldRange(
                newState.industrySelect,
                newState.sizeOfBusiness,
                getIndustryDataKey(field) as keyof IndustryDataEntry
              )

              // Calculate the middle value
              const defaultValue = Math.floor((range.min + range.max) / 2)
              newState[fieldKey] = defaultValue.toString()
            }
          })
        }
      }

      return newState
    })
  }

  const [showResultsView, setShowResultsView] = useState(showResults)

  // Update the handleScoresCalculated function
  const handleScoresCalculated = (scores: {
    profitability: string
    capitalManagement: string
    growthReadiness: string
  }) => {
    setCalculatedScores(scores)
    setScoresReady(true)

    // If we're already showing results, submit the form with scores
    if (showResultsView && onFormSubmit && !formSubmitted) {
      onFormSubmit({
        ...formData,
        profitability_score: scores.profitability,
        capital_management_score: scores.capitalManagement,
        growth_readiness_score: scores.growthReadiness,
      })
    }
  }

  // Update the handleNext function
  const handleNext = async (e: React.FormEvent) => {
    e.preventDefault()
    if (currentStep < totalSteps) {
      const nextStep = currentStep + 1
      setCurrentStep(nextStep)
      setHighestStepReached(Math.max(highestStepReached, nextStep))
    } else {
      try {
        // Show results view first, which will calculate scores
        if (!hideResultsScreen) {
          setShowResultsView(true)
          // The form submission will happen after scores are calculated
          // via the handleScoresCalculated function
          return
        }

        // If we're hiding results, we need to submit without scores
        if (onFormSubmit) {
          onFormSubmit(formData)
        }
      } catch (error) {
        console.error('Error submitting form:', error)
      }
    }
  }

  useEffect(() => {
    if (scoresReady && showResultsView && !formSubmitted) {
      // Submit the form with the calculated scores
      const submitForm = async () => {
        try {
          // Set formSubmitted to true to prevent duplicate submissions
          setFormSubmitted(true)

          const industryName = industryData[formData.industrySelect]?.name

          const utmParams = new URLSearchParams(window.location.search)
          const utmSource = utmParams.get('utm_source')
          const utmMedium = utmParams.get('utm_medium')
          const utmContent = utmParams.get('utm_content')
          const utmTerm = utmParams.get('utm_term')

          // Create the form data object with scores
          const formDataForSubmission = {
            retURL: '',
            full_name: `${formData.first_name || ''} ${formData.last_name || ''}`,
            first_name: formData.first_name || '',
            last_name: formData.last_name || '',
            email: formData.email || '',
            title: '',
            phone: formData.phone || '',
            zip: '',
            lead_source: 'Website',
            state: '',
            [process.env.NEXT_PUBLIC_CRM_FORM_FIELD_ID_SOURCE!]: window.location.href,
            company: formData.company || '',
            [process.env.NEXT_PUBLIC_CRM_FORM_FIELD_ID_CUSTOM_FIELD_1_QUESTION!]:
              'To what extent do your profits consistently convert into cashflow?',
            [process.env.NEXT_PUBLIC_CRM_FORM_FIELD_ID_CUSTOM_FIELD_1!]:
              formData.profit_consistency || '',
            [process.env.NEXT_PUBLIC_CRM_FORM_FIELD_ID_CUSTOM_FIELD_2_QUESTION!]:
              'How effectively do you track and manage cash flow separately from profit?',
            [process.env.NEXT_PUBLIC_CRM_FORM_FIELD_ID_CUSTOM_FIELD_2!]:
              formData.track_cashflow || '',
            [process.env.NEXT_PUBLIC_CRM_FORM_FIELD_ID_CUSTOM_FIELD_3_QUESTION!]:
              'How well do you feel the money invested in your business is generating strong returns?',
            [process.env.NEXT_PUBLIC_CRM_FORM_FIELD_ID_CUSTOM_FIELD_3!]:
              formData.q_mnoney_invested || '',
            [process.env.NEXT_PUBLIC_CRM_FORM_FIELD_ID_CUSTOM_FIELD_4_QUESTION!]:
              'How dependent is your business on outside funding to maintain operations or grow?',
            [process.env.NEXT_PUBLIC_CRM_FORM_FIELD_ID_CUSTOM_FIELD_4!]:
              formData.q_dep_outside_funding || '',
            [process.env.NEXT_PUBLIC_CRM_FORM_FIELD_ID_CUSTOM_FIELD_5_QUESTION!]:
              'How well is your business financially positioned to support growth and expansion?',
            [process.env.NEXT_PUBLIC_CRM_FORM_FIELD_ID_CUSTOM_FIELD_5!]:
              formData.q_futureGrowthAndExp || '',
            [process.env.NEXT_PUBLIC_CRM_FORM_FIELD_ID_CALCULATOR_FORM_INDUSTRY!]:
              industryName || '',
            [process.env.NEXT_PUBLIC_CRM_FORM_FIELD_ID_CALCULATOR_FORM_SALES!]:
              formData.sales || '',
            [process.env.NEXT_PUBLIC_CRM_FORM_FIELD_ID_CALCULATOR_FORM_CHANGE_IN_SALES!]:
              formData.change_in_sales || '',
            [process.env.NEXT_PUBLIC_CRM_FORM_FIELD_ID_CALCULATOR_FORM_NET_OPERATING_PROFIT!]:
              formData.net_operating_profit || '',
            [process.env
              .NEXT_PUBLIC_CRM_FORM_FIELD_ID_CALCULATOR_FORM_NET_OPERATING_PROFIT_MARGIN!]:
              formData.net_operating_profit_margin || '',
            [process.env.NEXT_PUBLIC_CRM_FORM_FIELD_ID_CALCULATOR_FORM_GROSS_MARGIN!]:
              formData.gross_margin || '',
            [process.env.NEXT_PUBLIC_CRM_FORM_FIELD_ID_CALCULATOR_FORM_WAGE_EXPENSE_TO_REVENUE!]:
              formData.wage_exp_percentage || '',
            [process.env
              .NEXT_PUBLIC_CRM_FORM_FIELD_ID_CALCULATOR_FORM_PROFITABILITY_AND_CASH_FLOW_ALIGNMENT!]:
              calculatedScores.profitability,
            [process.env.NEXT_PUBLIC_CRM_FORM_FIELD_ID_CALCULATOR_FORM_CAPITAL_MANAGEMENT_SCORE!]:
              calculatedScores.capitalManagement,
            [process.env.NEXT_PUBLIC_CRM_FORM_FIELD_ID_CALCULATOR_FORM_GROWTH_READINESS_SCORE!]:
              calculatedScores.growthReadiness,
            '00N0I00000Jxzgb': 'N/A',
            [process.env.NEXT_PUBLIC_CRM_FORM_FIELD_ID_CALCULATOR_FORM_SIZE_OF_BUSINESS!]:
              formData.sizeOfBusiness || '',
            '00N0I00000Jxzgc': '',
            '00N0I00000Jxzge': '',
            '00N0I00000Jxzgd': '',
            '00N0I00000JxhhR': 'Business',
            '00N2e000000OfRZ': 'Content Download',
            '00N0I00000Jxzgg': '',
            '00N2e000000P109': '701On00000JkoNPIAZ',
            utm_source: utmSource || '',
            '00N8t000000PAQE': utmSource || '',
            utm_medium: utmMedium || '',
            '00N8t000000PAQD': utmMedium || '',
            utm_content: utmContent || '',
            '00N8t000000PAQC': utmContent || '',
            utm_term: utmTerm || '',
            '00N8t000000Pa3s': utmTerm || '',
            '00N8t000000PQkk': '',
            '00N5m000000QwUf': '701On00000JkoNPIAZ',
            captcha_settings: {
              keyname: 'Google_Recaptcha',
              fallback: 'true',
            },
            'g-recaptcha-response': '',
            country: 'Australia',
            '0058t000000JuYqAAK': '',
            message: '',
            Source__c: window.location.href,
            oid: process.env.NEXT_PUBLIC_CRM_FORM_FIELD_VALUE_OID,
          }

          // Use ContactService to submit the form
          const contactService = new ContactService(process.env.NEXT_PUBLIC_LEADS_FORM_ENDPOINT)
          await contactService.submitForm(
            formDataForSubmission,
            `${process.env.NEXT_PUBLIC_LEADS_FORM_ENDPOINT}lead/submit`
          )
        } catch (error) {
          console.error('Error submitting form with scores:', error)
          // Reset formSubmitted if there's an error, allowing retry
          setFormSubmitted(false)
        }
      }

      submitForm()
    }
  }, [scoresReady, calculatedScores, showResultsView, formSubmitted])

  // Update URL when step changes
  useEffect(() => {
    if (!isClientSide) return

    const currentParams = new URLSearchParams(searchParams?.toString() || '')

    if (formSubmitted) {
      currentParams.set('step', 'results')
    } else if (currentStep === 1) {
      // Always remove step param for step 1
      currentParams.delete('step')
    } else if (currentStep >= highestStepReached) {
      // Only update step param if we're at the highest step reached and not on step 1
      currentParams.set('step', currentStep.toString())
      setHighestStepReached(currentStep)
    } else {
      // We're going backwards, remove the step param
      currentParams.delete('step')
    }

    // Update URL without reload
    window.history.replaceState(null, '', `${pathname}?${currentParams.toString()}`)
  }, [currentStep, formSubmitted, isClientSide, pathname, searchParams, highestStepReached])

  // Initialize step from URL on mount
  useEffect(() => {
    if (!isClientSide) return

    const stepFromUrl = searchParams?.get('step')
    if (stepFromUrl && !isNaN(Number(stepFromUrl))) {
      setCurrentStep(Number(stepFromUrl))
    }
  }, [searchParams, isClientSide])

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  // Helper function to map field names to industry data keys
  const getIndustryDataKey = (field: CalcFormField) => {
    // Map the field title to the corresponding industry data key
    const fieldMapping: { [key: string]: keyof IndustryDataEntry } = {
      'Net operating profit': 'net_operating_profit',
      Sales: 'sales',
      'Gross margin': 'gross_margin',
      'Net operating profit margin': 'net_operating_profit_margin',
      'Wage expense to revenue': 'wage_exp_percentage',
      // Add other mappings as needed
    }
    return fieldMapping[field.title]
  }

  // Update getFieldRange to use the extracted size
  const getFieldRange = (
    industryId: string,
    businessSize: string,
    fieldKey: keyof IndustryDataEntry
  ) => {
    // Default range for different metrics
    const defaultRanges: { [key: string]: { min: number; max: number } } = {
      net_operating_profit: { min: 0, max: 1000000 },
      sales: { min: 0, max: 10000000 },
      gross_margin: { min: 0, max: 100 }, // Percentage
      net_operating_profit_margin: { min: 0, max: 100 }, // Percentage
      wage_exp_percentage: { min: 0, max: 100 }, // Percentage
    }

    if (!industryId || !businessSize || !industryData[industryId]) {
      console.warn(`Using default range for: ${fieldKey}`)
      return defaultRanges[fieldKey] || { min: 0, max: 100 }
    }

    const industry = industryData[industryId]
    const sizeKey = businessSize.split(':')[0]
    const sizeData = industry.entries[sizeKey as keyof typeof industry.entries]

    if (!sizeData) {
      console.warn(`No size data found for key: ${sizeKey}, using default range`)
      return defaultRanges[fieldKey] || { min: 0, max: 100 }
    }

    // Get all values for this field
    const values = [
      parseFloat(String(sizeData['very-low']?.[fieldKey] ?? '0')),
      parseFloat(String(sizeData['low']?.[fieldKey] ?? '0')),
      parseFloat(String(sizeData['typical']?.[fieldKey] ?? '0')),
      parseFloat(String(sizeData['high']?.[fieldKey] ?? '0')),
      parseFloat(String(sizeData['very-high']?.[fieldKey] ?? '0')),
    ].filter((value) => !isNaN(value) && value >= 0)

    if (values.length === 0) {
      console.warn(`No valid values found for fieldKey: ${fieldKey}, using default range`)
      return defaultRanges[fieldKey] || { min: 0, max: 100 }
    }

    // Calculate range with some padding
    const min = Math.floor(Math.min(...values))
    const max = Math.ceil(Math.max(...values) * 1.2) // Add 20% to max for some headroom

    // Ensure the range doesn't exceed reasonable limits
    return {
      min: Math.max(0, min),
      max: Math.min(max, defaultRanges[fieldKey]?.max || 100),
    }
  }

  // Add handler to toggle between form and results
  const handleToggleView = () => {
    setShowResultsView(!showResultsView)
  }

  // Early return for results view - add hideResultsScreen check
  if (showResultsView && !hideResultsScreen) {
    return (
      <div className={styles.calculatorResultsContainer}>
        <Results
          isOpen={true}
          onClose={handleToggleView}
          formData={formData}
          processedFormFields={processedFormFields}
          industryData={industryData}
          ratings={ratings}
          printData={printData}
          onScoresCalculated={handleScoresCalculated}
          disclaimer={disclaimer}
          onDownload={onDownload}
        />
      </div>
    )
  }

  // Regular form view
  return (
    <TooltipPrimitive.Provider delayDuration={300}>
      <div className={styles.calculatorContainer}>
        <div className={styles.header}>
          <h2>Ready to see how your business stacks up?</h2>
          <p>
            Enter your details and key financial metrics to access your
            <br /> free business performance analysis.
          </p>
        </div>
        <h2 className={styles.title}>Industry Benchmarking Tool</h2>

        {/* Step indicator */}
        <div className={styles.stepIndicator}>
          {Object.keys(steps).map((step) => (
            <div
              key={step}
              className={`${styles.step} ${parseInt(step) === currentStep ? styles.active : ''}
                       ${parseInt(step) < currentStep ? styles.completed : ''}`}
            >
              Step {step}
            </div>
          ))}
        </div>

        <form onSubmit={handleNext} className={styles.form}>
          {/* Only show fields for current step */}
          {steps[currentStep]?.map((field: CalcFormField) => {
            if (field.component === 'calcFormHeading') {
              return (
                <h2 key={field._uid} className={styles.sectionHeading}>
                  {field.title}
                </h2>
              )
            }

            return (
              <div
                key={field._uid}
                className={
                  styles.formGroup + ' ' + (field.width === 'full' ? styles.fullWidth : '')
                }
              >
                <label htmlFor={field.key} className={styles.label + ' ' + styles[field.key]}>
                  <div className={styles.labelContainer}>
                    <div className={styles.label}>{field.title}</div>
                    {field.info && field.info.length > 0 && (
                      <TooltipPrimitive.Provider>
                        <TooltipPrimitive.Root delayDuration={0}>
                          <TooltipPrimitive.Trigger asChild>
                            <div className={styles.info} aria-label="Additional information">
                              <svg
                                width="15"
                                height="15"
                                viewBox="0 0 15 15"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <g clipPath="url(#clip0_144_122)">
                                  <path
                                    d="M7.5 0C3.35801 0 0 3.35801 0 7.5C0 11.642 3.35801 15 7.5 15C11.642 15 15 11.642 15 7.5C14.9956 3.35977 11.6402 0.00439453 7.5 0ZM7.5 13.7499C4.04824 13.7499 1.2501 10.9518 1.2501 7.5C1.2501 4.04824 4.04824 1.2501 7.5 1.2501C10.9518 1.2501 13.7499 4.04824 13.7499 7.5C13.7461 10.9503 10.95 13.7461 7.5 13.7499Z"
                                    fill="#0EACBD"
                                  />
                                  <path
                                    d="M7.4998 6.24993H6.8749C6.52979 6.24993 6.25 6.52971 6.25 6.87483C6.25 7.21995 6.52979 7.49973 6.8749 7.49973H7.4998V11.2497C7.4998 11.5948 7.77959 11.8746 8.12471 11.8746C8.46982 11.8746 8.74961 11.5948 8.74961 11.2497V7.49973C8.74961 6.8095 8.19004 6.24963 7.49951 6.24963L7.4998 6.24993Z"
                                    fill="#0EACBD"
                                  />
                                  <path
                                    d="M7.5 5.00012C8.01777 5.00012 8.4375 4.58039 8.4375 4.06262C8.4375 3.54486 8.01777 3.12512 7.5 3.12512C6.98223 3.12512 6.5625 3.54486 6.5625 4.06262C6.5625 4.58039 6.98223 5.00012 7.5 5.00012Z"
                                    fill="#0EACBD"
                                  />
                                </g>
                                <defs>
                                  <clipPath id="clip0_144_122">
                                    <rect width="15" height="15" fill="white" />
                                  </clipPath>
                                </defs>
                              </svg>
                            </div>
                          </TooltipPrimitive.Trigger>
                          <TooltipPrimitive.Portal>
                            <TooltipPrimitive.Content
                              className={styles.infoPopover}
                              sideOffset={5}
                              side="top"
                              align="center"
                            >
                              <div className={styles.infoIcon}>
                                <InfoIcon />
                              </div>
                              <div className={styles.infoContent}>
                                {/* This is magic to make everything before the ':' bold */}
                                {field.info && field.info.includes(':') ? (
                                  <>
                                    <strong>{field.info.split(':', 1)[0]}</strong>
                                    {': ' + field.info.substring(field.info.indexOf(':') + 1)}
                                  </>
                                ) : (
                                  field.info
                                )}
                              </div>
                            </TooltipPrimitive.Content>
                          </TooltipPrimitive.Portal>
                        </TooltipPrimitive.Root>
                      </TooltipPrimitive.Provider>
                    )}
                  </div>
                </label>
                {field.type === 'dropdown' && (
                  <PhnxFormselect
                    variant="valueLabel"
                    name={field.key}
                    options={
                      field.industryDataOptions
                        ? sortedIndustries.map((industry) => ({
                            value: industry._uid,
                            label: industry.name,
                          }))
                        : field.options
                            .split('\n')
                            .map((opt) => {
                              const [value, ...labelParts] = opt.split(':')
                              return {
                                value: value.trim(),
                                label: labelParts.join(':').trim(),
                              }
                            })
                            .sort((a, b) => {
                              if (field.key === 'sizeOfBusiness') {
                                return 0 // Keep original order
                              }
                              const numA = parseFloat(a.value)
                              const numB = parseFloat(b.value)
                              if (!isNaN(numA) && !isNaN(numB)) {
                                return numA - numB
                              }
                              return a.value.localeCompare(b.value)
                            })
                    }
                    isRequired
                    value={formData[field.key] || ''}
                    onChange={(value) => {
                      handleInputChange(field.key, value)
                    }}
                  />
                )}

                {field.type === 'slider' && (
                  <PhnxFormSlider
                    name={field.key}
                    isRequired
                    disabled={!formData.industrySelect || !formData.sizeOfBusiness}
                    min={
                      getFieldRange(
                        formData.industrySelect,
                        formData.sizeOfBusiness,
                        getIndustryDataKey(field) as keyof IndustryDataEntry
                      ).min
                    }
                    max={
                      getFieldRange(
                        formData.industrySelect,
                        formData.sizeOfBusiness,
                        getIndustryDataKey(field) as keyof IndustryDataEntry
                      ).max
                    }
                    value={parseFloat(formData[field.key] || '0')}
                    step={1}
                    prefix={field.prefix}
                    onChange={(value) => {
                      handleInputChange(field.key, value.toString())
                    }}
                  />
                )}

                {field.type !== 'dropdown' && field.type !== 'slider' && (
                  <div
                    className={`
                      ${styles.inputContainer}
                      ${field.key === 'name' ? styles.nameContainer : ''}
                      ${field.key === 'company' ? styles.fullWidth : ''}
                      ${field.key === 'contact' ? styles.contactContainer : ''}
                    `}
                  >
                    {field.key === 'name' && (
                      <>
                        <PhnxForminput
                          variant="text"
                          name="first_name"
                          placeholder="First name"
                          isRequired
                          key={field.key + 'first_name'}
                          value={formData.first_name || ''}
                          onChange={(value) => handleInputChange('first_name', value)}
                        />
                        <PhnxForminput
                          variant="text"
                          name="last_name"
                          placeholder="Last name"
                          isRequired
                          key={field.key + 'last_name'}
                          value={formData.last_name || ''}
                          onChange={(value) => handleInputChange('last_name', value)}
                        />
                      </>
                    )}

                    {field.key === 'company' && (
                      <PhnxForminput
                        variant="text"
                        name={'company'}
                        placeholder="Company name"
                        isRequired
                        key={field.key + 'company'}
                        value={formData[field.key] || ''}
                        onChange={(value) => handleInputChange(field.key, value)}
                      />
                    )}

                    {field.key === 'contact' && (
                      <>
                        <PhnxForminput
                          variant="text"
                          name="phone"
                          placeholder="Phone number"
                          isRequired
                          key={field.key + 'phone'}
                          value={formData.phone || ''}
                          onChange={(value) => handleInputChange('phone', value)}
                        />
                        <PhnxForminput
                          variant="email"
                          name="email"
                          placeholder="Email address"
                          isRequired
                          key={field.key + 'email'}
                          value={formData.email || ''}
                          onChange={(value) => handleInputChange('email', value)}
                        />
                      </>
                    )}

                    {/* if non of the above, render out the field with the prefix */}
                    {field.type !== 'dropdown' &&
                      field.type !== 'slider' &&
                      field.type !== 'text' && (
                        <div className={styles.inputContainer}>
                          <PhnxForminput
                            variant="number"
                            name={getFieldName(field)}
                            isRequired
                            value={formData[getFieldName(field)] || ''} // Ensure persisted value is used
                            onChange={(value) => {
                              handleInputChange(field.key, value)
                            }}
                          />
                          {field.prefix && <span className={styles.prefix}>{field.prefix}</span>}
                        </div>
                      )}
                  </div>
                )}
              </div>
            )
          })}

          <div className={styles.navigationButtons}>
            {currentStep > 1 && (
              <PhnxButton variant="tertiary" label="Back" onClick={handlePrevious} type="button" />
            )}
            <PhnxButton
              variant="primary"
              label={currentStep === totalSteps ? 'Get my results' : 'Submit and continue'}
              type="submit"
            />
          </div>

          <div className={styles.disclaimer}>{disclaimer}</div>
        </form>
      </div>
    </TooltipPrimitive.Provider>
  )
}

export default CalculatorForm
